import React, { useState, useEffect, useRef } from 'react'
import { Row, Col, Toast } from '@cleartrip/bento'
import { Divider } from '@cleartrip/bento/components/Divider'
import { formatCurrency } from 'Utils'
import { getCurrencyByLocale } from 'AccountsUtils/transform'
import {
  apiGetHelper,
  apiPostHelper,
  checkMobileDevice,
  readCookie
} from '../../../utils'
import {
  GET_WALLET_INFO,
  GET_WALLET_TRANSACTIONS,
  QITAF_SEND_OTP_ENDPOINT,
  QITAF_REDEEM_ENDPOINT
} from '../../../constants/api_endpoints'
import './index.scss'
import { getMappedValue } from '../../../utils/arabicUtils'

function WalletDashboard() {
  const [activeTab, setActiveTab] = useState('EARNED')
  const [transactions, setTransactions] = useState([])
  const [walletInfo, setWalletInfo] = useState(null)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [transactionsFetched, setTransactionsFetched] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [loadMoreText, setLoadMoreText] = useState(
    getMappedValue('Load more transactions')
  )
  const [showLoadMore, setShowLoadMore] = useState(false)
  const [totalRecords, setTotalRecords] = useState(0)
  const [currency, setCurrency] = useState(null)
  const [showWalletBalance, setShowWalletBalance] = useState(true)

  // Qitaf flow states
  const [mobileNumber, setMobileNumber] = useState('')
  const [mobileError, setMobileError] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [otp, setOtp] = useState('')
  const [otpError, setOtpError] = useState('')
  const [qitafAmount, setQitafAmount] = useState('')
  const [amountError, setAmountError] = useState('')
  const [isLoadingOtp, setIsLoadingOtp] = useState(false)
  const [isLoadingRedeem, setIsLoadingRedeem] = useState(false)
  const [trackId, setTrackId] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  const offsetRef = useRef({
    offset: 1,
    pageSize: 10
  })
  const handleBackClick = () => {
    window.location.href = '/'
  }

  const getSourceType = (sourceType, status) => {
    let sourceTypeText = ''
    switch (sourceType) {
      case 'F':
        sourceTypeText = getMappedValue('Flight')
        break
      case 'H':
        sourceTypeText = getMappedValue('Hotel')
        break
      case 'FPH':
        sourceTypeText = getMappedValue('Flights and Hotel')
        break
      case 'FC':
        sourceTypeText = getMappedValue('Flyin Cash')
        break
      case 'Qitaf':
        sourceTypeText = getMappedValue('Qitaf')
        break
      case 'WCB':
        sourceTypeText = getMappedValue('Cashback')
        break
      default:
        sourceTypeText = sourceType
    }

    // For WCB transactions, append status in brackets if available
    if (sourceType === 'WCB' && status) {
      return `${sourceTypeText} (${status.toLowerCase()})`
    }

    return sourceTypeText
  }

  const getTransactionDate = dateString => {
    const date = new Date(dateString)
    return date
      .toISOString()
      .split('T')[0]
      .split('-')
      .reverse()
      .join('-')
  }

  const fetchWalletInfo = async () => {
    try {
      const userIdCookie = readCookie('userid')
      const userId = decodeURIComponent(userIdCookie)?.split('|')[4]
      const defaultCurObj = getCurrencyByLocale()
      const utmCurr = readCookie('cur')
      const defaultCurrency = utmCurr
      const { hostname } = window.location
      const chnl = hostname.includes('flyin') ? 'Fly' : 'Ct'

      const res = await apiGetHelper(
        `${GET_WALLET_INFO}?userId=${userId}&currency=${defaultCurrency}&chnl=${chnl}`
      )
      if (res && res.data) {
        // Priority check: Check status field first
        if (res.data.status === 'FAILURE') {
          // Failure case: Store msg and hide balance/transactions
          setErrorMessage(
            res.data.msg || getMappedValue('Unable to fetch wallet details')
          )
          setShowWalletBalance(false)
          return
        }

        // Success case: Proceed with normal display
        if (res.data.status === 'SUCCESS') {
          setWalletInfo(res.data.balance)
          setCurrency(res.data.currency)
          setShowWalletBalance(true)
        }
      }
    } catch (err) {
      console.error('Error in fetchWalletInfo:', err)
      setShowError(true)
      setErrorMessage(
        getMappedValue('Unable to fetch wallet details! Please try again.')
      )
    }
  }

  const fetchTransactions = async (isLoadMore = false, tabName = activeTab) => {
    try {
      if (!isLoadMore) {
        setTransactionsFetched(false)
        setTransactions([])
        offsetRef.current.offset = 1
      } else {
        setIsLoadingMore(true)
      }

      const userIdCookie = readCookie('userid')
      const userId = decodeURIComponent(userIdCookie)?.split('|')[4]
      const defaultCurObj = getCurrencyByLocale()
      const utmCurr = readCookie('cur')
      const defaultCurrency = utmCurr
      const { hostname } = window.location
      const chnl = hostname.includes('flyin') ? 'Fly' : 'Ct'
      const currentCurrency = currency || readCookie('cur')

      const typeMap = {
        EARNED: 'EARNED',
        USED: 'USED',
        REFUND: 'REFUND'
      }

      const currentOffset = offsetRef.current.offset

      const res = await apiGetHelper(
        `${GET_WALLET_TRANSACTIONS}${userId}/transactions?offset=${currentOffset}&size=${offsetRef.current.pageSize}&currency=${currentCurrency}&type=${typeMap[tabName]}&chnl=${chnl}`
      )

      if (res?.data) {
        if (res.data.totalRecords !== undefined) {
          setTotalRecords(res.data.totalRecords)
        }

        const newTransactions = isLoadMore
          ? [...transactions, ...(res.data.transactions || [])]
          : [...(res.data.transactions || [])]

        setTransactions(newTransactions)

        const currentLoaded = newTransactions.length
        const totalAvailable = res.data.totalRecords || totalRecords
        const hasMore = currentLoaded < totalAvailable

        console.log(
          'Loaded transactions:',
          currentLoaded,
          'Total records:',
          totalAvailable,
          'Has more:',
          hasMore
        )

        setShowLoadMore(hasMore)

        if (isLoadMore) {
          offsetRef.current.offset += 1
        } else {
          offsetRef.current.offset = 2
        }
      } else {
        setShowLoadMore(false)
        if (!isLoadMore) {
          setTransactions([])
        }
      }
    } catch (err) {
      console.error('Error in fetchTransactions:', err)
      setShowError(true)
      setErrorMessage(
        getMappedValue('Unable to fetch transactions! Please try again.')
      )
    } finally {
      setTransactionsFetched(true)
      setIsLoadingMore(false)
      setLoadMoreText('Load more transactions')
    }
  }

  const loadMoreTransactions = () => {
    setLoadMoreText(getMappedValue('loading...'))
    fetchTransactions(true)
  }

  const handleTabChange = tabName => {
    if (activeTab === tabName) return

    setTransactions([])
    setShowLoadMore(false)
    setTransactionsFetched(false)

    if (tabName === 'TOP UP WITH QITAF') {
      setTransactionsFetched(true)
      // Reset Qitaf flow when tab is selected
      setMobileNumber('')
      setMobileError('')
      setOtpSent(false)
      setOtp('')
      setOtpError('')
      setQitafAmount('')
      setAmountError('')
      setTrackId('')
      setShowSuccess(false)
    } else {
      offsetRef.current.offset = 1
      setActiveTab(tabName)
      fetchTransactions(false, tabName)
    }

    setActiveTab(tabName)
  }

  // Validate mobile number (only check for empty)
  const validateMobileNumber = number => {
    if (!number) {
      setMobileError(getMappedValue('Mobile number is required'))
      return false
    }
    setMobileError('')
    return true
  }

  // Validate OTP
  const validateOtp = otpValue => {
    if (!otpValue) {
      setOtpError(getMappedValue('OTP is required'))
      return false
    }
    setOtpError('')
    return true
  }

  // Validate amount
  const validateAmount = amount => {
    if (!amount) {
      setAmountError(getMappedValue('Amount is required'))
      return false
    }
    if (isNaN(amount) || parseFloat(amount) <= 0) {
      setAmountError(getMappedValue('Please enter a valid amount'))
      return false
    }
    setAmountError('')
    return true
  }

  // Handle generating OTP
  const handleGenerateOtp = async () => {
    if (!validateMobileNumber(mobileNumber)) {
      return
    }

    setIsLoadingOtp(true)
    try {
      const userIdCookie = readCookie('userid')
      const userId = decodeURIComponent(userIdCookie)?.split('|')[4] || '123456'

      // Using apiGetHelper for OTP generation
      const otpUrl = `${QITAF_SEND_OTP_ENDPOINT}?mobile=${mobileNumber}&tripRef=null&userUniqueId=${userId}`
      const isMobileDevice = checkMobileDevice()
      const response = await apiPostHelper(
        otpUrl,
        {},
        isMobileDevice,
        'application/json'
      )

      const data = response.data

      if (data.status === 'S' && data.description === 'success') {
        setOtpSent(true)
        setTrackId(data.trackId)
        setSuccessMessage(
          getMappedValue('OTP has been sent to your mobile number')
        )
        setShowSuccess(true)
        setTimeout(() => setShowSuccess(false), 3000)
      } else if (data.status === 'F') {
        // Replace 'MSISDN' with 'Mobile number' in the error description
        const desc = (data.description || '').replace(
          /MSISDN/g,
          'Mobile number'
        )
        setMobileError(desc)
      } else {
        setShowError(true)
        setErrorMessage(getMappedValue('Not a Qitaf Registered Number.'))
      }
    } catch (error) {
      console.error(getMappedValue('Error sending OTP:', error))
      setShowError(true)
      setErrorMessage(getMappedValue('Failed to send OTP. Please try again.'))
    } finally {
      setIsLoadingOtp(false)
    }
  }

  // Handle redeeming Qitaf points
  const handleRedeemQitaf = async () => {
    if (!validateOtp(otp) || !validateAmount(qitafAmount)) {
      return
    }

    setIsLoadingRedeem(true)
    try {
      const userIdCookie = readCookie('userid')
      const userId = decodeURIComponent(userIdCookie)?.split('|')[4] || '123456'

      const username = userId.split('|')[0]
      const usrStrng = username.includes('%40')
        ? decodeURIComponent(username.split('%257C')[0].replace('%40', '@'))
        : username
      const disName = usrStrng.includes('|') ? usrStrng.split('|')[0] : usrStrng
      const userEmail = disName || 'user@flyin.com'

      const currencyCode = 'SAR'

      // Using apiGetHelper for redemption API call
      const redeemUrl = `${QITAF_REDEEM_ENDPOINT}?otp=${otp}&mobile=${mobileNumber}&currency=${currencyCode}&tripRef=null&amount=${qitafAmount}&email=${encodeURIComponent(
        userEmail
      )}&userUniqueId=${userId}`
      const isMobileDevice = checkMobileDevice()
      const response = await apiPostHelper(
        redeemUrl,
        {},
        isMobileDevice,
        'application/json'
      )

      const data = response.data

      if (data.description === 'success') {
        // Reset form after successful redemption
        setMobileNumber('')
        setOtp('')
        setQitafAmount('')
        setOtpSent(false)
        setSuccessMessage(getMappedValue('Qitaf points redeemed successfully!'))
        setShowSuccess(true)
        setTimeout(() => setShowSuccess(false), 3000)

        // Refresh wallet balance
        fetchWalletInfo()
      } else {
        setShowError(true)
        setErrorMessage(
          getMappedValue('Failed to redeem Qitaf points. Please try again.')
        )
      }
    } catch (error) {
      console.error('Error redeeming Qitaf points:', error)
      setShowError(true)
      setErrorMessage(
        getMappedValue('Failed to redeem Qitaf points. Please try again.')
      )
    } finally {
      setIsLoadingRedeem(false)
    }
  }

  useEffect(() => {
    const initializeData = async () => {
      await fetchWalletInfo()
      await fetchTransactions(false)
    }
    initializeData()
  }, [])

  const getQitafTopUpContent = () => {
    return (
      <div className="pl-4 pr-4 pt-4">
        <h2 className="fs-4 fw-bold mb-3">
          {getMappedValue('Add Money to your wallet Using Qitaf Points')}
        </h2>
        <p className="fs-3 c-grey-555 mb-5">
          {getMappedValue(
            'Top-up your Flyin Wallet using Qitaf points which will never expire with us. Qitaf points moved to wallet cannot be refunded but can be used for Flyin Bookings.'
          )}
        </p>

        {!otpSent ? (
          <div className="mb-4">
            <label className="fs-3 fw-medium mb-2 d-block">
              {getMappedValue('Enter your STC mobile number')}
            </label>
            <div className="flex">
              <input
                type="text"
                className={`form-control flex-grow-1 mr-3 ml-3 ${
                  mobileError ? 'error-border' : ''
                }`}
                style={{
                  padding: '12px',
                  border: mobileError ? '1px solid #ff0000' : '1px solid #ccc',
                  borderRadius: '4px',
                  width: '100%',
                  maxWidth: '600px'
                }}
                value={mobileNumber}
                onChange={e => {
                  const value = e.target.value
                  // Allow only numbers
                  if (/^\d*$/.test(value)) {
                    setMobileNumber(value)
                    if (value) validateMobileNumber(value)
                  }
                }}
                placeholder="e.g. 555240680"
                maxLength={9}
              />
              <button
                className="btn fs-3 fw-medium"
                style={{
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  padding: '0 24px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  opacity: isLoadingOtp ? 0.7 : 1
                }}
                onClick={handleGenerateOtp}
                disabled={isLoadingOtp}
              >
                {isLoadingOtp
                  ? getMappedValue('SENDING...')
                  : getMappedValue('GENERATE OTP')}
              </button>
            </div>
            {mobileError && (
              <div className="fs-3 c-red mt-1">{mobileError}</div>
            )}
            <p className="fs-3 c-blue mt-2">
              {getMappedValue(
                'Enter your Qitaf registered mobile number above and click on Generate OTP. You will receive an SMS code required to complete your payment.'
              )}
            </p>
          </div>
        ) : (
          <div>
            <div className="mb-4">
              <label className="fs-3 fw-medium mb-2 d-block">
                {getMappedValue('Enter OTP received on')} {mobileNumber}
              </label>
              <div className="flex mb-3">
                <input
                  type="text"
                  className={`form-control flex-grow-1 mr-3 ${
                    otpError ? 'error-border' : ''
                  }`}
                  style={{
                    padding: '12px',
                    border: otpError ? '1px solid #ff0000' : '1px solid #ccc',
                    borderRadius: '4px',
                    width: '100%',
                    maxWidth: '600px'
                  }}
                  value={otp}
                  onChange={e => {
                    const value = e.target.value
                    // Allow only numbers
                    if (/^\d*$/.test(value)) {
                      setOtp(value)
                      if (value) validateOtp(value)
                    }
                  }}
                  placeholder={getMappedValue('Enter OTP')}
                  maxLength={6}
                />
              </div>
              {otpError && <div className="fs-3 c-red mb-3">{otpError}</div>}

              <label className="fs-3 fw-medium mb-2 d-block">
                {getMappedValue('Enter Amount to Redeem')}
              </label>
              <div className="flex mb-3">
                <input
                  type="text"
                  className={`form-control flex-grow-1 mr-3 ml-3 ${
                    amountError ? 'error-border' : ''
                  }`}
                  style={{
                    padding: '12px',
                    border: amountError
                      ? '1px solid #ff0000'
                      : '1px solid #ccc',
                    borderRadius: '4px',
                    width: '100%',
                    maxWidth: '600px'
                  }}
                  value={qitafAmount}
                  onChange={e => {
                    const value = e.target.value
                    // Allow only numbers and decimal point
                    if (/^\d*\.?\d*$/.test(value)) {
                      setQitafAmount(value)
                      if (value) validateAmount(value)
                    }
                  }}
                  placeholder={getMappedValue('Enter amount')}
                />
                <button
                  className="btn fs-3 fw-medium"
                  style={{
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    padding: '0 24px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    opacity: isLoadingRedeem ? 0.7 : 1
                  }}
                  onClick={handleRedeemQitaf}
                  disabled={isLoadingRedeem}
                >
                  {isLoadingRedeem
                    ? getMappedValue('PROCESSING...')
                    : getMappedValue('REDEEM')}
                </button>
              </div>
              {amountError && (
                <div className="fs-3 c-red mb-3">{amountError}</div>
              )}

              <div className="flex mt-3">
                <button
                  className="btn fs-3 fw-medium"
                  style={{
                    backgroundColor: '#f1f1f1',
                    color: '#333',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    marginRight: '10px',
                    marginLeft: '10px'
                  }}
                  onClick={() => {
                    setOtpSent(false)
                    setOtp('')
                    setQitafAmount('')
                  }}
                >
                  {getMappedValue('BACK')}
                </button>
                <button
                  className="btn fs-3 fw-medium"
                  style={{
                    backgroundColor: '#2196F3',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    opacity: isLoadingOtp ? 0.7 : 1
                  }}
                  onClick={handleGenerateOtp}
                  disabled={isLoadingOtp}
                >
                  {isLoadingOtp
                    ? getMappedValue('RESENDING...')
                    : getMappedValue('RESEND OTP')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const getTransactionsTable = () => {
    return (
      <div>
        <If condition={Array.isArray(transactions) && transactions.length > 0}>
          <div className="ml-2 mr-2 mt-2 fs-2 bg-grey-f1 bb bb-light-grey-color table-header">
            <Row>
              <Col span={5}>{getMappedValue('Source')}</Col>
              <Col span={5}>{getMappedValue('Transaction ID')}</Col>
              <Col span={5}>{getMappedValue('Transaction Type')}</Col>
              <Col span={4}>{getMappedValue('Amount')}</Col>
              <Col span={5}>{getMappedValue('Expiry Date')}</Col>
            </Row>
          </div>
          {transactions.map(transaction => (
            <div
              key={transaction.txnId}
              className="ml-4 mr-4 pl-1 pr-1 pt-4 bb bb-light-grey-color table-cell"
            >
              <Row>
                <Col span={5}>
                  {getSourceType(transaction.sourceType, transaction.status)}
                  <br />
                  {(transaction.sourceType == 'F' ||
                    transaction.sourceType == 'H' ||
                    transaction.sourceType == 'WCB') &&
                    transaction.refernceNumber && (
                      <p style={{ color: 'grey', fontSize: '10px' }}>
                        {getMappedValue('ID')}: {transaction.refernceNumber}
                      </p>
                    )}
                </Col>
                <Col span={5}>{transaction.txnId}</Col>
                <Col span={5}>{getMappedValue(transaction.txnType)}</Col>
                <Col span={4}>
                  <div className="amount-cell">
                    {formatCurrency(
                      transaction.txnWalletAmt,
                      transaction?.currency
                    )}
                  </div>
                </Col>
                <Col span={5}>
                  {transaction.txnExpireDate
                    ? getTransactionDate(transaction.txnExpireDate)
                    : '-'}
                </Col>
              </Row>
            </div>
          ))}
          {showLoadMore && !isLoadingMore && (
            <div
              className="c-blue pt-1 pb-4 fs-3 ml-4 c-pointer"
              onClick={loadMoreTransactions}
            >
              {loadMoreText}
            </div>
          )}
          {isLoadingMore && (
            <div className="flex flex-center p-4">
              <div className="Pageloader__spinner" />
            </div>
          )}
          <Else />
          <div className="ml-4 mt-2 fs-2 c-grey-888">
            {getMappedValue("You don't have any transactions")}
          </div>
        </If>
      </div>
    )
  }

  const getTransactionsTableMweb = () => {
    return (
      <div className="wallet-transactions-mweb">
        <If condition={Array.isArray(transactions) && transactions.length > 0}>
          {transactions.map(transaction => (
            <div
              key={transaction.txnId}
              className="wallet-card mb-4 bg-white br-4 p-4"
              style={{ borderRadius: '8px' }}
            >
              <div className="flex flex-between mb-3">
                <div>
                  <div className="wallet-icon-container">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke="#1A1A1A"
                        strokeDasharray="1 1"
                        d="M6.5 4L6.5 20"
                      />
                      <path
                        fill="#1A1A1A"
                        d="M22 9.5h.5A.5.5 0 0022 9v.5zm0 5v.5a.5.5 0 00.5-.5H22zm-5-3a.5.5 0 000 1v-1zm.01 1a.5.5 0 000-1v1zM4 4.5h16v-1H4v1zM21.5 6v12h1V6h-1zM20 19.5H4v1h16v-1zM2.5 18V6h-1v12h1zM4 19.5A1.5 1.5 0 012.5 18h-1A2.5 2.5 0 004 20.5v-1zM21.5 18a1.5 1.5 0 01-1.5 1.5v1a2.5 2.5 0 002.5-2.5h-1zM20 4.5A1.5 1.5 0 0121.5 6h1A2.5 2.5 0 0020 3.5v1zm-16-1A2.5 2.5 0 001.5 6h1A1.5 1.5 0 014 4.5v-1zM16.5 10H22V9h-5.5v1zm5-.5v5h1v-5h-1zM22 14h-5.5v1H22v-1zm-5.5 0a2 2 0 01-2-2h-1a3 3 0 003 3v-1zm0-5a3 3 0 00-3 3h1a2 2 0 012-2V9zm.5 3.5h.01v-1H17v1z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="text-right">
                  <div className="fs-6 fw-bold mb-1">
                    {formatCurrency(
                      transaction.txnWalletAmt,
                      transaction?.currency
                    )}
                  </div>
                  {transaction.txnExpireDate && (
                    <div className="fs-3 c-grey-555">
                      {getMappedValue('Expiry on')} :{' '}
                      {getTransactionDate(transaction.txnExpireDate)}
                    </div>
                  )}
                </div>
              </div>

              <div className="transaction-details">
                <div className="fs-4 fw-medium mb-2">
                  {getSourceType(transaction.sourceType, transaction.status)}
                </div>
                {transaction.sourceType === 'WCB' &&
                  transaction.refernceNumber && (
                    <div className="fs-3 c-grey-555 mb-1">
                      {getMappedValue('ID')}: {transaction.refernceNumber}
                    </div>
                  )}
                <div className="fs-3 c-grey-555 mb-1">
                  {getMappedValue('Transaction ID:')} {transaction.txnId}
                </div>
                <div className="fs-3 c-grey-555">
                  {getMappedValue('Date:')}{' '}
                  {getTransactionDate(transaction.txnDate)}
                </div>
              </div>
            </div>
          ))}

          {showLoadMore && !isLoadingMore && (
            <div
              className="c-blue pt-1 pb-4 fs-3 text-center c-pointer"
              onClick={loadMoreTransactions}
            >
              {loadMoreText}
            </div>
          )}

          {isLoadingMore && (
            <div className="flex flex-center p-4">
              <div className="Pageloader__spinner" />
            </div>
          )}

          <Else />
          <div className="text-center mt-4 fs-2 c-grey-888 pl-2 mb-5 pl-5">
            {getMappedValue("You don't have any transactions")}
          </div>
        </If>
      </div>
    )
  }

  return (
    <div
      className="flex flex-left"
      style={{
        width: '100%',
        backgroundColor: '#fff',
        overflow: 'scroll',
        flexDirection: 'column',
        minHeight: '600px'
      }}
    >
      <div
        className="top-container"
        style={{
          border: 'none',
          minWidth: checkMobileDevice() ? '0px' : '640px'
        }}
      >
        <If condition={showWalletBalance}>
          <div className="bg-blue-wallet flex flex-between">
            <div
              className="pt-5 pb-5 pl-4 pr-4 fs-5"
              style={{ color: 'black' }}
            >
              {getMappedValue('My Wallet Balance')}
            </div>
            <div
              className="pr-4 pl-4 fs-5 pt-5 pb-5"
              style={{ color: 'black' }}
            >
              {walletInfo !== undefined && walletInfo !== null
                ? formatCurrency(walletInfo, currency)
                : 'SAR 0'}
            </div>
          </div>
          <div>
            <div
              className="flex"
              style={{ marginTop: '20px', backgroundColor: '#fff' }}
            >
              {[
                'EARNED',
                'USED',
                'REFUND'
                // , 'TOP UP WITH QITAF'
              ].map(tab => (
                <div
                  key={tab}
                  className={`wallet-tab ${activeTab === tab ? 'active' : ''}`}
                  style={{
                    padding: checkMobileDevice() ? '12px 14px' : '12px 24px'
                  }}
                  onClick={() => handleTabChange(tab)}
                >
                  {getMappedValue(tab)}
                </div>
              ))}
            </div>
            <Divider className="mt-0" />
            <If condition={transactionsFetched}>
              {activeTab === 'TOP UP WITH QITAF'
                ? getQitafTopUpContent()
                : window.innerWidth < 768
                ? getTransactionsTableMweb()
                : getTransactionsTable()}
              <Else />
              <div className="flex flex-center">
                <div className="Pageloader__spinner" />
              </div>
            </If>
          </div>
          <Else />
          <div
            className="flex flex-center flex-middle"
            style={{
              minHeight: '400px',
              padding: '40px 20px'
            }}
          >
            <div
              className="fs-5"
              style={{ color: 'black', textAlign: 'center' }}
            >
              {errorMessage}
            </div>
          </div>
        </If>
      </div>
      {showError && (
        <div>
          <Toast
            message={errorMessage}
            onClose={() => setShowError(false)}
            className={`row mt-15 ${
              checkMobileDevice()
                ? 'w-80p z-10 walletErrorToastMobile'
                : 'walletErrorToast w-585 ml-5'
            }`}
            closeBtn={true}
            error={true}
          />
        </div>
      )}
      {showSuccess && (
        <Toast
          message={successMessage}
          onClose={() => setShowSuccess(false)}
          className="row mt-15 ml-5 w-585 bg-green walletSuccessToast"
          closeBtn={true}
          error={false}
        />
      )}
    </div>
  )
}

export default WalletDashboard
